import { db } from '@/lib/firebase'
import type { Interaction, Post } from '@/types'
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'

export interface InteractionNotificationData {
  postId: string
  postTitle: string
  postAuthorId: string
  interactionType: 'like' | 'comment' | 'share'
  interactionCount: number
  senderName?: string
  senderId?: string
}

export class InteractionNotificationService {
  private readonly notificationsCollection = 'notifications'
  private readonly interactionsCollection = 'interactions'

  /**
   * Create or update notification for post interactions
   * Groups multiple interactions of the same type on the same post
   */
  async createOrUpdateInteractionNotification(interaction: Interaction, post: Post): Promise<void> {
    console.log('🔔 createOrUpdateInteractionNotification called:', {
      interactionType: interaction.type,
      postId: post.id,
      postAuthorId: post.authorId,
      interactorId: interaction.userId,
    })

    if (!post.authorId || !post.id || !post.title) {
      console.log('❌ Missing required data:', { authorId: post.authorId, postId: post.id, title: post.title })
      return
    }

    try {
      // Get count of this type of interaction on this post
      const interactionCount = await this.getInteractionCount(post.id, interaction.type)
      console.log('📊 Interaction count:', interactionCount)

      // Check if there's already a notification for this post and interaction type
      const existingNotification = await this.findExistingNotification(post.authorId, post.id, interaction.type)
      console.log('🔍 Existing notification:', existingNotification ? 'Found' : 'None')

      const notificationData: InteractionNotificationData = {
        postId: post.id,
        postTitle: post.title,
        postAuthorId: post.authorId,
        interactionType: interaction.type,
        interactionCount,
        senderName: interaction.userName,
        senderId: interaction.userId,
      }

      if (existingNotification) {
        // Update existing notification
        console.log('🔄 Updating existing notification')
        await this.updateNotification(existingNotification.id, notificationData)
      } else {
        // Create new notification
        console.log('✨ Creating new notification')
        await this.createNotification(post.authorId, notificationData)
      }
    } catch (error) {
      console.error('❌ Error creating/updating interaction notification:', error)
    }
  }

  /**
   * Get count of interactions of a specific type on a post
   */
  private async getInteractionCount(postId: string, type: 'like' | 'comment' | 'share'): Promise<number> {
    try {
      const q = query(
        collection(db, this.interactionsCollection),
        where('postId', '==', postId),
        where('type', '==', type),
      )
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (error) {
      return 0
    }
  }

  /**
   * Find existing notification for post interaction
   * Looks for notifications from the last 24 hours regardless of read status
   */
  private async findExistingNotification(
    userId: string,
    postId: string,
    interactionType: 'like' | 'comment' | 'share',
  ): Promise<{ id: string } | null> {
    try {
      // Look for notifications from the last 24 hours regardless of read status
      const twentyFourHoursAgo = new Date()
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24)

      // Use a simpler query that doesn't require the complex composite index
      const q = query(
        collection(db, this.notificationsCollection),
        where('userId', '==', userId),
        where('type', '==', 'engagement'),
        where('subtype', '==', `post_${interactionType}`),
        where('data.postId', '==', postId),
        limit(5), // Get a few recent ones to check
      )

      const snapshot = await getDocs(q)

      if (!snapshot.empty) {
        // Find the most recent notification within 24 hours
        for (const doc of snapshot.docs) {
          const notificationData = doc.data()
          const createdAt = notificationData.createdAt?.toDate?.() || new Date(0)

          if (createdAt > twentyFourHoursAgo) {
            return { id: doc.id }
          }
        }
      }

      return null
    } catch (error) {
      // If the query fails due to missing index, just return null to create a new notification
      return null
    }
  }

  /**
   * Create new interaction notification
   */
  private async createNotification(userId: string, data: InteractionNotificationData): Promise<void> {
    try {
      const message = this.generateNotificationMessage(data)

      const notificationDoc = {
        userId,
        type: 'engagement',
        subtype: `post_${data.interactionType}`,
        message,
        data: {
          postId: data.postId,
          postTitle: data.postTitle,
          interactionType: data.interactionType,
          interactionCount: data.interactionCount,
          senderName: data.senderName,
          senderId: data.senderId,
        },
        audience: 'all',
        read: false,
        createdAt: serverTimestamp(),
      }

      const docRef = await addDoc(collection(db, this.notificationsCollection), notificationDoc)
      console.log('✅ Notification created:', {
        id: docRef.id,
        userId,
        type: data.interactionType,
        message,
      })
    } catch (error) {
      console.error('❌ Error creating notification:', error)
    }
  }

  /**
   * Update existing interaction notification
   * Only marks as unread if the notification was already read (new activity)
   */
  private async updateNotification(notificationId: string, data: InteractionNotificationData): Promise<void> {
    try {
      console.log('🔄 Updating notification:', notificationId)
      const message = this.generateNotificationMessage(data)

      const docRef = doc(db, this.notificationsCollection, notificationId)

      // Get current notification to check if it's read
      const currentDoc = await getDoc(docRef)
      const currentData = currentDoc.data()
      const isCurrentlyRead = currentData?.read === true
      console.log('📖 Current notification state:', { isCurrentlyRead, currentMessage: currentData?.message })

      const updateData: any = {
        message,
        data: {
          postId: data.postId,
          postTitle: data.postTitle,
          interactionType: data.interactionType,
          interactionCount: data.interactionCount,
          senderName: data.senderName,
          senderId: data.senderId,
        },
        updatedAt: serverTimestamp(), // Track when notification was last updated
      }

      // Only mark as unread if there's new activity on a previously read notification
      if (isCurrentlyRead) {
        updateData.read = false
        console.log('📬 Marking notification as unread (new activity on read notification)')
      } else {
        console.log('📭 Keeping notification unread')
      }

      await updateDoc(docRef, updateData)
      console.log('✅ Notification updated:', { id: notificationId, message })
    } catch (error) {
      console.error('❌ Error updating notification:', error)
    }
  }

  /**
   * Generate notification message based on interaction count
   */
  private generateNotificationMessage(data: InteractionNotificationData): string {
    const { postTitle, interactionType, interactionCount, senderName } = data

    const truncatedTitle = postTitle.length > 50 ? `${postTitle.substring(0, 50)}...` : postTitle

    if (interactionCount === 1) {
      const actionText =
        interactionType === 'like' ? 'liked' : interactionType === 'comment' ? 'commented on' : 'shared'
      return `${senderName || 'Someone'} ${actionText} your post "${truncatedTitle}"`
    }

    const actionText = interactionType === 'like' ? 'likes' : interactionType === 'comment' ? 'comments' : 'shares'
    return `Your post "${truncatedTitle}" has ${interactionCount} ${actionText}`
  }
}

export const interactionNotificationService = new InteractionNotificationService()
